import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { normalizeAvatarUrl } from "@/lib/avatarUrl";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { inicialesDe } from "@/lib/iniciales";
import { useFotoReintento } from "@/hooks/useFotoReintento";
import { Skeleton } from "@/components/ui/skeleton";

const TAMANO = {
  xs: "size-6 text-[10px]",
  sm: "size-8 text-[11px]",
  md: "size-10 text-[13px]",
  lg: "size-12 text-[15px]",
  xl: "size-16 text-xl",
} as const;

/**
 * Avatar de una persona (usuario, cliente, agente, colaborador): la foto de perfil
 * (`usuarios.foto_perfil_url`) si existe y carga; si no, sus iniciales en el círculo verde tenue.
 * Si la URL falla (404, sin permiso), Radix cae solo a las iniciales.
 *
 * Es la única pieza para pintar a alguien: no se arman círculos de iniciales a mano. Para traer
 * la foto de otra persona, `useFotosPerfil` (`hooks/useFotosPerfil`).
 */
export function AvatarPersona({
  nombre,
  fotoUrl,
  size = "md",
  enmascarado = false,
  className,
  claseIniciales,
  enLinea,
  ultimaActividad,
  cargando = false,
}: {
  nombre: string | null | undefined;
  fotoUrl?: string | null;
  size?: keyof typeof TAMANO;
  /** Modo presentación: ni foto ni iniciales. */
  enmascarado?: boolean;
  className?: string;
  /** Color del círculo de iniciales cuando significa algo (estado de una cita, RSVP). */
  claseIniciales?: string;
  /** Presencia: punto verde si está en línea. Sin la prop (o `null`, desconocido) no se pinta punto. */
  enLinea?: boolean | null;
  /** Última actividad (ISO) para el tooltip cuando no está en línea. */
  ultimaActividad?: string | null;
  /** Esqueleto circular mientras llega la foto (nunca un círculo vacío). */
  cargando?: boolean;
}) {
  const foto = enmascarado ? "" : normalizeAvatarUrl(fotoUrl);
  const { intento, alEstado } = useFotoReintento(foto);
  const avatar = cargando ? (
    <Skeleton className={cn("shrink-0 rounded-full", TAMANO[size], className)} aria-hidden />
  ) : (
    // Con punto de estado (encabezado y pie del menú) lleva borde: una foto transparente o blanca
    // no deja un círculo invisible sobre el fondo.
    <Avatar className={cn("shrink-0", enLinea != null && "bg-muted ring-1 ring-border", TAMANO[size], className)}>
      {foto && <AvatarImage key={intento} src={foto} onLoadingStatusChange={alEstado} alt={nombre ? `Foto de ${nombre}` : "Foto de perfil"} className="object-cover" />}
      <AvatarFallback aria-hidden={!!nombre} className={cn("bg-primary/10 font-semibold text-primary", claseIniciales)}>
        {enmascarado ? "••" : inicialesDe(nombre) || "?"}
      </AvatarFallback>
    </Avatar>
  );
  if (enLinea == null) return avatar;
  const textoEstado = enLinea
    ? "En línea"
    : ultimaActividad
      ? `Última actividad: ${formatDistanceToNow(new Date(ultimaActividad), { locale: es, addSuffix: true })}`
      : "Sin actividad reciente";
  return (
    // Tooltip del estándar (InfoTooltip), no el `title` nativo del navegador.
    <InfoTooltip content={textoEstado}>
    <span className="relative inline-flex shrink-0">
      {avatar}
      <span
        role="img"
        aria-label={textoEstado}
        className={cn(
          "absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full ring-2 ring-card",
          enLinea ? "bg-success" : "bg-muted-foreground/50",
        )}
      />
    </span>
    </InfoTooltip>
  );
}
